import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analyticsApi, auditApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, FileText, Clock, Heart,
  LogOut, LayoutDashboard, ArrowLeft, Activity, Shield, ChevronDown
} from 'lucide-react';
import { LogoHome } from '../components/LogoHome';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

const CHART_COLORS = ['#0D9488', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function AnalyticsDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30');
  const [overview, setOverview] = useState(null);
  const [reportsOverTime, setReportsOverTime] = useState([]);
  const [complianceOverTime, setComplianceOverTime] = useState([]);
  const [workerPerformance, setWorkerPerformance] = useState([]);
  const [templateUsage, setTemplateUsage] = useState([]);
  const [wellbeing, setWellbeing] = useState(null);
  const [clockSummary, setClockSummary] = useState(null);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const days = parseInt(period);
      const [overviewRes, reportsRes, compRes, workersRes, templateRes, wellbeingRes, clockRes, auditRes] =
        await Promise.all([
          analyticsApi.getOverview(days),
          analyticsApi.getReportsOverTime(days),
          analyticsApi.getComplianceOverTime(days),
          analyticsApi.getWorkerPerformance(days),
          analyticsApi.getTemplateUsage(),
          analyticsApi.getWellbeingSummary(days),
          analyticsApi.getClockSummary(days),
          auditApi.getTrail({ days, limit: 20 })
        ]);
      setOverview(overviewRes.data);
      setReportsOverTime(reportsRes.data);
      setComplianceOverTime(compRes.data);
      setWorkerPerformance(workersRes.data);
      setTemplateUsage(templateRes.data);
      setWellbeing(wellbeingRes.data);
      setClockSummary(clockRes.data);
      setAuditTrail(auditRes.data.events || []);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const backRoute = user?.role === 'organisation' ? '/admin' : '/supervisor';

  const statusData = overview?.status_breakdown
    ? Object.entries(overview.status_breakdown).map(([name, value]) => ({
        name: name.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value
      }))
    : [];

  const formatTemplateName = (type) => {
    if (!type) return 'Unknown';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="analytics-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950 border-r border-slate-800 z-50 hidden lg:flex lg:flex-col">
        <div className="p-6 pb-2 flex-shrink-0">
          <LogoHome className="h-16 w-auto mb-4" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          <nav className="space-y-1">
            <Link to={backRoute} className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-teal-600/20 text-teal-400 border border-teal-600/30">
              <BarChart3 className="h-5 w-5" />
              Analytics
            </div>
            <Link to="/audit-trail" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Activity className="h-5 w-5" />
              Audit Trail
            </Link>
            <Link to="/report-builder" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="h-5 w-5 text-amber-400" />
              Report Builder
            </Link>
          </nav>
        </div>
        <div className="flex-shrink-0 p-6 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{user?.name}</p>
              <p className="text-slate-500 text-xs capitalize">{user?.role}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white" data-testid="analytics-title">Analytics Dashboard</h1>
              <p className="text-slate-400 mt-1">Organisation performance insights and reporting trends</p>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white" data-testid="period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-teal-600/20">
                    <FileText className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="total-reports">{overview?.total_reports || 0}</p>
                    <p className="text-xs text-slate-400">Total Reports</p>
                  </div>
                </div>
                <p className="text-xs text-teal-400 mt-2">{overview?.recent_reports || 0} in period</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-blue-600/20">
                    <Users className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="total-workers">{overview?.total_workers || 0}</p>
                    <p className="text-xs text-slate-400">Workers</p>
                  </div>
                </div>
                <p className="text-xs text-blue-400 mt-2">{overview?.total_clients || 0} clients</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-emerald-600/20">
                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="avg-compliance">{overview?.compliance?.average || 0}%</p>
                    <p className="text-xs text-slate-400">Avg Compliance</p>
                  </div>
                </div>
                <p className="text-xs text-emerald-400 mt-2">Range: {overview?.compliance?.min || 0}% - {overview?.compliance?.max || 0}%</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-amber-600/20">
                    <Clock className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="total-hours">{clockSummary?.total_hours || 0}h</p>
                    <p className="text-xs text-slate-400">Total Hours</p>
                  </div>
                </div>
                <p className="text-xs text-amber-400 mt-2">{clockSummary?.total_shifts || 0} shifts logged</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="reports" className="space-y-6">
            <TabsList className="bg-slate-800 border border-slate-700">
              <TabsTrigger value="reports" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-slate-400">Reports</TabsTrigger>
              <TabsTrigger value="workers" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-slate-400">Workers</TabsTrigger>
              <TabsTrigger value="wellbeing" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-slate-400">Wellbeing</TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white text-slate-400">Audit Trail</TabsTrigger>
            </TabsList>

            {/* Reports Tab */}
            <TabsContent value="reports" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Reports Over Time */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Reports Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reportsOverTime.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={reportsOverTime}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                          <Bar dataKey="total" fill="#0D9488" radius={[4, 4, 0, 0]} name="Total" />
                          <Bar dataKey="submitted" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Submitted" />
                          <Bar dataKey="approved" fill="#10B981" radius={[4, 4, 0, 0]} name="Approved" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No report data in this period</div>
                    )}
                  </CardContent>
                </Card>

                {/* Status Breakdown Pie */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Report Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {statusData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={statusData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {statusData.map((entry, index) => (
                              <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No reports yet</div>
                    )}
                  </CardContent>
                </Card>

                {/* Compliance Trend */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Compliance Score Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {complianceOverTime.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={complianceOverTime}>
                          <defs>
                            <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#0D9488" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#0D9488" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} domain={[0, 100]} />
                          <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                          <Area type="monotone" dataKey="average_score" stroke="#0D9488" fill="url(#compGrad)" strokeWidth={2} name="Avg Score" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No compliance data yet</div>
                    )}
                  </CardContent>
                </Card>

                {/* Template Usage */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Template Usage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {templateUsage.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={templateUsage.map(t => ({ ...t, name: formatTemplateName(t.template_type) }))} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="name" type="category" stroke="#94A3B8" tick={{ fontSize: 11 }} width={120} />
                          <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                          <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Reports" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[280px] flex items-center justify-center text-slate-500">No template usage data</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Workers Tab */}
            <TabsContent value="workers" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">Active Workers</p>
                    <p className="text-3xl font-bold text-white mt-1">{overview?.total_workers || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">Total Shifts</p>
                    <p className="text-3xl font-bold text-white mt-1">{clockSummary?.total_shifts || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">Avg Hours/Shift</p>
                    <p className="text-3xl font-bold text-white mt-1">{clockSummary?.avg_hours_per_shift || 0}h</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-base">Worker Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  {workerPerformance.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="text-slate-400">Worker</TableHead>
                          <TableHead className="text-slate-400">Reports</TableHead>
                          <TableHead className="text-slate-400">Submitted</TableHead>
                          <TableHead className="text-slate-400">Approved</TableHead>
                          <TableHead className="text-slate-400">Avg Compliance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {workerPerformance.map((w) => (
                          <TableRow key={w.worker_id} className="border-slate-700">
                            <TableCell className="text-white font-medium">{w.worker_name}</TableCell>
                            <TableCell className="text-slate-300">{w.total_reports}</TableCell>
                            <TableCell className="text-slate-300">{w.submitted}</TableCell>
                            <TableCell className="text-slate-300">{w.approved}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                w.avg_compliance >= 85 ? 'border-emerald-500 text-emerald-400' :
                                w.avg_compliance >= 70 ? 'border-amber-500 text-amber-400' :
                                'border-red-500 text-red-400'
                              }>
                                {w.avg_compliance}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No worker performance data in this period</p>
                  )}
                </CardContent>
              </Card>

              {/* Worker Hours Chart */}
              {clockSummary?.worker_hours?.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Hours by Worker</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={clockSummary.worker_hours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="worker_name" stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Bar dataKey="total_hours" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Hours" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Wellbeing Tab */}
            <TabsContent value="wellbeing" className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">Check-ins</p>
                    <p className="text-3xl font-bold text-white mt-1">{wellbeing?.total_checkins || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">Avg Mood</p>
                    <p className="text-3xl font-bold text-emerald-400 mt-1">{wellbeing?.avg_mood || 0}/10</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">Avg Stress</p>
                    <p className="text-3xl font-bold text-amber-400 mt-1">{wellbeing?.avg_stress || 0}/10</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-5">
                    <p className="text-sm text-slate-400">High Stress Alerts</p>
                    <p className="text-3xl font-bold text-red-400 mt-1">{wellbeing?.high_stress_count || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {wellbeing?.trend?.length > 0 && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Mood & Stress Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={wellbeing.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} domain={[0, 10]} />
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                        <Legend />
                        <Line type="monotone" dataKey="avg_mood" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} name="Mood" />
                        <Line type="monotone" dataKey="avg_stress" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} name="Stress" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {(!wellbeing?.trend || wellbeing.trend.length === 0) && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-12 text-center">
                    <Heart className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No wellbeing check-ins in this period</p>
                    <p className="text-slate-500 text-sm mt-1">Workers can submit check-ins from the Wellbeing Hub</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Audit Trail Tab */}
            <TabsContent value="audit" className="space-y-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-base">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {auditTrail.length > 0 ? (
                    <div className="space-y-3">
                      {auditTrail.map((event) => (
                        <div key={event.id} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-800" data-testid="audit-event">
                          <div className="p-1.5 rounded bg-slate-800 mt-0.5">
                            <Activity className="h-3.5 w-3.5 text-teal-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white text-sm font-medium">{event.user_name || 'System'}</span>
                              <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                                {event.action?.replace(/_/g, ' ')}
                              </Badge>
                              <Badge variant="outline" className="border-teal-600/50 text-teal-400 text-xs">
                                {event.entity_type}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(event.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
                              {event.entity_id && <span className="ml-2 text-slate-600">ID: {event.entity_id?.slice(0, 8)}...</span>}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No audit events in this period. Actions like creating, updating, and submitting reports are tracked here.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
